import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { operations } from '@luxuryescapes/contract-svc-bedbank/server';

import { SUPPLIER_NAMES } from '~/consts/bedbank';

import useCurrentUser from '~/hooks/useCurrentUser';

import bedbankService from '~/services/BedbankService';

import { reportError } from '~/utils/reportError';

import RankRow, { RankEdit } from './RankRow';

type PropertyRankResult =
  operations['/api/bedbank/properties/{propertyId}/rank/get']['responses']['200']['content']['application/json']['result'];
type PatchPayload =
  operations['/api/bedbank/properties/{propertyId}/rank/patch']['requestBody']['content']['application/json'];
type SupplierRanks = PropertyRankResult['suppliers'];
type SupplierKey = keyof SupplierRanks;
type PropertyRank = NonNullable<PatchPayload['rank']>;
type PropertyRankOverride = NonNullable<PatchPayload['override']>;
type SupplierRankOverride = NonNullable<NonNullable<PatchPayload['suppliers']>[SupplierKey]>['override'];

const PROPERTY_OVERRIDE_OPTIONS: Array<NonNullable<PropertyRankOverride>> = ['A', 'B', 'C', 'New'];
const SUPPLIER_OVERRIDE_OPTIONS: Array<NonNullable<SupplierRankOverride>> = ['High', 'Medium', 'Low', 'New'];

function formatSupplierName(supplier: string): string {
  return supplier
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normaliseDate(value?: string | null): string {
  return value?.slice(0, 10) ?? '';
}

function toIsoDate(value: string | null): string | null {
  return value ? `${value}T00:00:00.000Z` : null;
}

function buildInitialPropertyEdit(rank: PropertyRankResult): RankEdit {
  return {
    override: rank.override ?? '',
    overrideReason: rank.overrideReason ?? '',
    overrideUntil: normaliseDate(rank.overrideUntil),
  };
}

function buildInitialSupplierEdits(suppliers: SupplierRanks): Partial<Record<SupplierKey, RankEdit>> {
  return Object.entries(suppliers).reduce<Partial<Record<SupplierKey, RankEdit>>>((acc, [supplier, rank]) => {
    acc[supplier as SupplierKey] = {
      override: rank.override ?? '',
      overrideReason: rank.overrideReason ?? '',
      overrideUntil: normaliseDate(rank.overrideUntil),
    };
    return acc;
  }, {});
}

function getChangedRankEdit(
  current: RankEdit,
  original: RankEdit,
): { override?: string | null; overrideReason?: string | null; overrideUntil?: string | null } | null {
  const payload: { override?: string | null; overrideReason?: string | null; overrideUntil?: string | null } = {};
  const currentOverride = current.override || null;
  const originalOverride = original.override || null;
  const currentOverrideReason = current.override ? current.overrideReason.trim() || null : null;
  const originalOverrideReason = original.overrideReason || null;
  const currentOverrideUntil = current.override ? current.overrideUntil || null : null;
  const originalOverrideUntil = original.overrideUntil || null;

  if (currentOverride !== originalOverride) {
    payload.override = currentOverride;
  }
  if (currentOverrideReason !== originalOverrideReason) {
    payload.overrideReason = currentOverrideReason;
  }
  if (currentOverrideUntil !== originalOverrideUntil) {
    payload.overrideUntil = toIsoDate(currentOverrideUntil);
  }

  return Object.keys(payload).length > 0 ? payload : null;
}

interface Props {
  propertyId: string;
}

export default function RankPane({ propertyId }: Props) {
  const { user: currentUser } = useCurrentUser();
  const [rank, setRank] = useState<PropertyRankResult | null>(null);
  const [propertyEdit, setPropertyEdit] = useState<RankEdit | null>(null);
  const [supplierEdits, setSupplierEdits] = useState<Partial<Record<SupplierKey, RankEdit>>>({});
  const [supplierToAdd, setSupplierToAdd] = useState<SupplierKey | ''>('');
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentUserName = currentUser?.fullName || currentUser?.email;

  const fetchRank = useCallback(async () => {
    setFetching(true);
    setError(null);
    try {
      const response = await bedbankService.getPropertyRank({ propertyId });
      setRank(response.result);
      setPropertyEdit(buildInitialPropertyEdit(response.result));
      setSupplierEdits(buildInitialSupplierEdits(response.result.suppliers));
    } catch (err) {
      setError('Failed to load rank details');
      reportError(err);
    } finally {
      setFetching(false);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchRank();
  }, [fetchRank]);

  const patchPayload = useMemo<PatchPayload>(() => {
    if (!rank || !propertyEdit) {
      return {};
    }

    const payload: PatchPayload = {};
    const propertyChanges = getChangedRankEdit(propertyEdit, buildInitialPropertyEdit(rank));
    if (propertyChanges) {
      Object.assign(payload, {
        ...(!rank.rank && propertyEdit.override ? { rank: 'New' as PropertyRank } : {}),
        ...propertyChanges,
      });
    }

    const supplierChanges = Object.entries(supplierEdits).reduce<NonNullable<PatchPayload['suppliers']>>(
      (acc, [supplier, edit]) => {
        const originalRank = rank.suppliers[supplier as SupplierKey];
        if (!edit) {
          return acc;
        }

        const changes = getChangedRankEdit(edit, {
          override: originalRank?.override ?? '',
          overrideReason: originalRank?.overrideReason ?? '',
          overrideUntil: normaliseDate(originalRank?.overrideUntil),
        });
        if (changes) {
          acc[supplier as SupplierKey] = {
            ...(!originalRank ? { rank: 'New' } : {}),
            ...changes,
          } as NonNullable<NonNullable<PatchPayload['suppliers']>[SupplierKey]>;
        }
        return acc;
      },
      {},
    );

    if (Object.keys(supplierChanges).length > 0) {
      payload.suppliers = supplierChanges;
    }

    return payload;
  }, [propertyEdit, rank, supplierEdits]);

  const hasChanges = Object.keys(patchPayload).length > 0;
  const hasMissingRequiredOverrideReason =
    Boolean(propertyEdit?.override && !propertyEdit.overrideReason.trim()) ||
    Object.values(supplierEdits).some((edit) => Boolean(edit?.override && !edit.overrideReason.trim()));

  const availableSuppliersToAdd = useMemo<Array<SupplierKey>>(() => {
    return SUPPLIER_NAMES.filter((supplier) => !supplierEdits[supplier]);
  }, [supplierEdits]);

  const handleAddSupplierRank = () => {
    if (!supplierToAdd) {
      return;
    }

    setSupplierEdits((current) => ({
      ...current,
      [supplierToAdd]: {
        override: '',
        overrideReason: '',
        overrideUntil: '',
      },
    }));
    setSupplierToAdd('');
  };

  const handleSave = async () => {
    if (!hasChanges || hasMissingRequiredOverrideReason) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await bedbankService.patchPropertyRank({ propertyId }, patchPayload);
      setRank(response.result);
      setPropertyEdit(buildInitialPropertyEdit(response.result));
      setSupplierEdits(buildInitialSupplierEdits(response.result.suppliers));
    } catch (err) {
      setError('Failed to save rank details');
      reportError(err);
    } finally {
      setSaving(false);
    }
  };

  if (fetching && !rank) {
    return <Typography>Loading rank details...</Typography>;
  }

  if (!rank || !propertyEdit) {
    return (
      <Stack gap={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <Button variant="outlined" onClick={fetchRank} disabled={fetching}>
          Retry
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <RankRow
        label="Property rank"
        rank={rank.rank ?? (propertyEdit.override ? 'New' : undefined)}
        updated={rank.updated}
        overrideUpdated={rank.overrideUpdated}
        edit={propertyEdit}
        overrideOptions={PROPERTY_OVERRIDE_OPTIONS}
        currentUserName={currentUserName}
        onChange={setPropertyEdit}
      />

      <Box>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} mb={2}>
          <Typography variant="h6">Supplier ranks</Typography>
          {availableSuppliersToAdd.length > 0 && (
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              <TextField
                select
                label="Add supplier"
                value={supplierToAdd}
                onChange={(event) => setSupplierToAdd(event.target.value as SupplierKey)}
                size="small"
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">Select supplier</MenuItem>
                {availableSuppliersToAdd.map((supplier) => (
                  <MenuItem key={supplier} value={supplier}>
                    {formatSupplierName(supplier)}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="outlined" onClick={handleAddSupplierRank} disabled={!supplierToAdd}>
                Add supplier rank
              </Button>
            </Stack>
          )}
        </Stack>
        <Stack gap={2}>
          {Object.entries(supplierEdits).map(([supplier, edit]) => {
            const key = supplier as SupplierKey;
            const supplierRank = rank.suppliers[key];
            if (!edit) {
              return null;
            }

            return (
              <RankRow
                key={supplier}
                label={formatSupplierName(supplier)}
                rank={supplierRank?.rank ?? 'New'}
                updated={supplierRank?.updated}
                overrideUpdated={supplierRank?.overrideUpdated}
                edit={edit}
                overrideOptions={SUPPLIER_OVERRIDE_OPTIONS}
                currentUserName={currentUserName}
                onChange={(nextEdit) =>
                  setSupplierEdits((current) => ({
                    ...current,
                    [key]: nextEdit,
                  }))
                }
              />
            );
          })}
        </Stack>
      </Box>

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || hasMissingRequiredOverrideReason || saving}
        >
          {saving ? 'Saving...' : 'Save rank'}
        </Button>
      </Box>
    </Stack>
  );
}
